import { ControllerResponse } from '@/DTOS/Controller/ControllerResponse';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { useQuery } from '@tanstack/react-query';
import { Alert, Input, Spin, Table, Tabs } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthProvider';
import KPIService from '../../services/kpi/KPIService';
import { getBaseUrl } from '../../utils/baseUrl';
import './KPIDynamic.css';

// Data interfaces
interface KPIRecord {
  id: string;
  date: string;
  value: string;
  kpiDefineId: string;
  kpiDefineLabel: string;
  kpiDefineAction: string; // join | average | sum | etc
  kpiDefineSection: string;
  kpiDefineLabelOrder: number;
  kpiDefineIsLastDay: boolean; // weekend summary flag
  kpiDefineLabelTranslate: string;
}

interface KPIColumn {
  title: string;
  dataIndex?: string;
  key: string;
  render?: (text: any, record: any) => React.ReactNode;
}

interface KPIData {
  id: string;
  date: string;
  weeks: { currents: KPIRecord[] };
}

// ========= i18n for Tabs =========
// English weekday -> { grouping key, Romanian label }
const dayI18n: Record<string, { key: 'luni' | 'marti' | 'miercuri' | 'joi' | 'vineri' | 'weekend'; ro: string }> = {
  Monday: { key: 'luni', ro: 'Luni' },
  Tuesday: { key: 'marti', ro: 'Marți' },
  Wednesday: { key: 'miercuri', ro: 'Miercuri' },
  Thursday: { key: 'joi', ro: 'Joi' },
  Friday: { key: 'vineri', ro: 'Vineri' },
  Saturday: { key: 'weekend', ro: 'Weekend' }, // Sat/Sun grouped as Weekend
  Sunday: { key: 'weekend', ro: 'Weekend' },
};

// Group key -> both-language label
const tabLabelMap: Record<
  'luni' | 'marti' | 'miercuri' | 'joi' | 'vineri' | 'weekend',
  { en: string; ro: string }
> = {
  luni: { en: 'Monday', ro: 'Luni' },
  marti: { en: 'Tuesday', ro: 'Marti' },
  miercuri: { en: 'Wednesday', ro: 'Miercuri' },
  joi: { en: 'Thursday', ro: 'Joi' },
  vineri: { en: 'Friday', ro: 'Vineri' },
  weekend: { en: 'Weekend', ro: '' },
};

const TAB_ORDER: Array<keyof typeof tabLabelMap> = ['luni', 'marti', 'miercuri', 'joi', 'vineri', 'weekend'];

const KPIDynamic: React.FC = () => {
  const { profile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // Extract current section from URL
  const getSectionFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get('section') || params.get('section?') || 'empty';
  };
  const section = getSectionFromUrl();

  // Permission check
  const hasPermission = () => {
    const roles = profile?.userPermission?.rols || [];
    if (roles.includes('admin') || roles.includes('kpi_admin')) return true;
    const permissionMap: Record<string, string[]> = {
      Marketing: ['kpi_marketing', 'kpi_admin'],
      Summary: ['kpi_summary', 'kpi_admin'],
      Financial: ['kpi_financial', 'kpi_admin'],
      Sales: ['kpi_sales', 'kpi_admin'],
      Operations: ['kpi_operations', 'kpi_admin'],
      'IT & Support': ['kpi_it', 'kpi_admin'],
      'HR (summary)': ['kpi_hr', 'kpi_admin'],
      'Alerts / Risks': ['kpi_alert', 'kpi_admin'],
    };
    return permissionMap[section]?.some((r) => roles.includes(r)) ?? false;
  };

  // State: ready when roles loaded
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (profile?.userPermission?.rols) setReady(true);
  }, [profile]);

  // Redirect if unauthorized
  useEffect(() => {
    if (ready && !hasPermission()) navigate('/home');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, section]);

  // Fetch KPI data for the section
  const { data: kpiData, isFetching, isError, error } = useQuery({
    queryKey: ['kpi', section],
    queryFn: () =>
      KPIService.search({ section: section, isLastWeek: true }).then(
        (res: ControllerResponse<KPIData[]>) => res.response,
      ),
    enabled: ready && hasPermission(),
    refetchOnWindowFocus: false,
  });

  // Local editing state
  const [editingValues, setEditingValues] = useState<Record<string, string>>({});
  useEffect(() => {
    if (kpiData?.[0]?.weeks?.currents) {
      const initial = Object.fromEntries(
        kpiData[0].weeks.currents.map((item) => [item.id, item.value]),
      );
      setEditingValues(initial);
    }
  }, [kpiData]);

  // SignalR connection reference and connection state
  const connRef = useRef<HubConnection | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const prevSectionRef = useRef(section);

  useEffect(() => {
    const hubUrl = `${getBaseUrl()}/hubs/kpi`;

    // Create connection
    const connection = new HubConnectionBuilder().withUrl(hubUrl).withAutomaticReconnect().build();

    // Handlers
    connection.onclose(() => setIsConnected(false));
    connection.onreconnected(() => setIsConnected(true));
    connection.on('SectionUpdated', (updates: KPIRecord[]) => {
      setEditingValues((prev) => {
        const next = { ...prev };
        updates.forEach((u) => {
          next[u.id] = u.value;
        });
        return next;
      });
    });

    connection
      .start()
      .then(() => {
        setIsConnected(true);
        return connection.invoke('JoinSection', section);
      })
      .catch((err: any) => console.error('SignalR start error:', err));

    connRef.current = connection;

    return () => {
      connection.invoke('LeaveSection', prevSectionRef.current).catch(() => {});
      connection.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-join group if section changes
  useEffect(() => {
    const conn = connRef.current;
    if (conn?.state === HubConnectionState.Connected && prevSectionRef.current !== section) {
      conn.invoke('LeaveSection', prevSectionRef.current).catch(() => {});
      conn.invoke('JoinSection', section).catch((err: any) => console.error(err));
      prevSectionRef.current = section;
    }
  }, [section]);

  // Update single KPI on change (debounced per field)
  const handleValueChange = (item: KPIRecord, newValue: string) => {
    setEditingValues((prev) => ({ ...prev, [item.id]: newValue }));

    if (debounceTimers.current[item.id]) {
      clearTimeout(debounceTimers.current[item.id]);
    }

    debounceTimers.current[item.id] = setTimeout(() => {
      const conn = connRef.current;
      if (conn?.state === HubConnectionState.Connected && kpiData?.[0]?.id) {
        conn
          .invoke('UpsertWeek', {
            id: kpiData[0].id,
            week: {
              currents: [
                {
                  id: item.id,
                  value: newValue,
                  kpiDefineSection: item.kpiDefineSection,
                },
              ],
            },
          })
          .catch((err: any) => console.error('UpsertWeek error:', err));
      }
    }, 200);
  };

  // ======= Helpers =======
  const groupBySection = (arr: KPIRecord[]) =>
    arr.reduce((acc, r) => {
      (acc[r.kpiDefineSection] ||= []).push(r);
      return acc;
    }, {} as Record<string, KPIRecord[]>);

  const groupByDayKey = (arr: KPIRecord[]) => {
    const out: Record<string, KPIRecord[]> = {};
    arr.forEach((r) => {
      const enDay = new Date(r.date).toLocaleDateString('en-US', { weekday: 'long' });
      const key = dayI18n[enDay]?.key ?? 'weekend';
      (out[key] ||= []).push(r);
    });
    return out;
  };

  const getRegularItems = (arr: KPIRecord[]) =>
    arr
      .filter((r) => !r.kpiDefineIsLastDay)
      .sort((a, b) => a.kpiDefineLabelOrder - b.kpiDefineLabelOrder);

  const getLastDayItems = (arr: KPIRecord[]) =>
    arr
      .filter((r) => r.kpiDefineIsLastDay)
      .sort((a, b) => a.kpiDefineLabelOrder - b.kpiDefineLabelOrder);

  const buildDayColumns = (records: KPIRecord[]) => {
    const sections = groupBySection(getRegularItems(records));
    const maxCols = Math.max(0, ...Object.values(sections).map((s) => s.length));
    const cols: KPIColumn[] = [{ title: 'Section', dataIndex: 'section', key: 'section' }];

    for (let i = 0; i < maxCols; i++) {
      cols.push({
        title: `KPI ${i + 1}`,
        key: `kpi${i + 1}`,
        render: (_: any, row: any) => {
          const recs: KPIRecord[] = row[`col${i}`] || [];
          if (!recs.length) return null;
          const item = recs[0];
          const val = editingValues[item.id] ?? item.value;
          const action = (item.kpiDefineAction || '').toLowerCase();

          const label = (
            <div>
              <b>{item.kpiDefineLabel}</b>
              <br />
              <i>{item.kpiDefineLabelTranslate}</i>
            </div>
          );

          if (action === 'join')
            return (
              <>
                {label}
                <Input.TextArea value={val} onChange={(e) => handleValueChange(item, e.target.value)} rows={2} />
              </>
            );

          if (['average', 'sum'].includes(action))
            return (
              <>
                {label}
                <Input type='number' value={val} onChange={(e) => handleValueChange(item, e.target.value)} />
              </>
            );

          return (
            <>
              {label}
              <Input.TextArea value={val} onChange={(e) => handleValueChange(item, e.target.value)} rows={2} />
            </>
          );
        },
      });
    }

    return cols;
  };

  const buildDataSource = (records: KPIRecord[]) => {
    const sections = groupBySection(getRegularItems(records));
    const maxCols = Math.max(0, ...Object.values(sections).map((s) => s.length));
    return Object.entries(sections).map(([sec, arr]) => {
      const row: any = { section: sec };
      for (let i = 0; i < maxCols; i++) {
        row[`col${i}`] = arr[i] ? [arr[i]] : [];
      }
      return row;
    });
  };

  // Memoize to avoid re-computing heavy stuff on each render
  const tabItems = useMemo(() => {
    const allRecords = kpiData?.[0]?.weeks?.currents || [];
    const byDay = groupByDayKey(allRecords);

    const hasRegular = getRegularItems(allRecords).length > 0;

    const orderedKeys = (hasRegular ? TAB_ORDER : (['weekend'] as typeof TAB_ORDER)).filter(
      (k) => (byDay[k]?.length || 0) > 0,
    );

    return orderedKeys.map((key) => {
      const { en, ro } = tabLabelMap[key];
      const dayRecords = byDay[key] || [];
      const regularsForDay = getRegularItems(dayRecords);
      const columns = buildDayColumns(dayRecords);
      const dataSource = buildDataSource(dayRecords);
      const weekendSummaries = getLastDayItems(allRecords);

      return {
        key,
        label: ro ? `${en} (${ro})` : en, // e.g. "Monday (Luni)"
        children: (
          <div>
            {regularsForDay.length > 0 && (
              <Table
                dataSource={dataSource}
                columns={columns}
                pagination={false}
                rowKey='section'
                size='middle'
                scroll={{ x: 400 }}
                bordered
                className='kpi-table'
              />
            )}

            {key === 'weekend' &&
              weekendSummaries.map((item) => (
                <div key={item.id} style={{ margin: '1em 0' }}>
                  <b>{item.kpiDefineLabel}</b>
                  <br />
                  <i>{item.kpiDefineLabelTranslate}</i>
                  <br />
                  <Input.TextArea
                    rows={4}
                    value={editingValues[item.id] ?? item.value}
                    onChange={(e) => handleValueChange(item, e.target.value)}
                  />
                </div>
              ))}
          </div>
        ),
      } as const;
    });
  }, [kpiData, editingValues]);

  // Loading & error states
  if (!ready || isFetching || !isConnected) {
    return <Spin size='large' className='spinner' />;
  }

  if (isError) {
    return <Alert message={(error as Error).message} type='error' />;
  }

  return (
    <div className='kpiTableWrap' style={{ padding: 20 }}>
      <Tabs items={tabItems as any} defaultActiveKey='luni' />
    </div>
  );
};

export default KPIDynamic;
